import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { push } from 'react-router-redux';

import Box from '@quintoandar/cozy-core/Box';
import StatusList, { status } from '@quintoandar/cozy-core/StatusList';
import Typography from '@quintoandar/cozy-core/Typography';
import Progress from '@quintoandar/cozy-core/Progress';
import ROUTES from 'routes/constants';
import { resumeAssessment } from 'containers/PropertyAssessment/actions';
import useCanAssessProperty from 'data/react-query/queries/useCanAssessProperty';
import usePhotoSession from 'data/react-query/queries/usePhotoSession';

import {
  houseStepClickedAnalytics,
  externalAreaClickedAnalytics,
  generalConditionsClickedAnalytics,
} from '../actions';
import messages from './messages';

const isChecklistFinished = (percentageDone) =>
  percentageDone === 100 ? 'secondary' : undefined;

const getItensStatusList = ({
  photoSessionId,
  houseId,
  houseStepClicked,
  generalConditionsStepClicked,
  externalAreaStepClicked,
  isSectionsFinished,
  isNewChecklist,
  canAssessProperty,
  houseArea,
}) => [
  {
    icon: () => 1,
    title: messages.externalAreaTitle,
    description: messages.externalAreaDescription,
    onClick: externalAreaStepClicked({
      photoSessionId,
      houseId,
      isNewChecklist,
    }),
    chevron: true,
    status: isSectionsFinished.externalArea ? status.SUCCESS : undefined,
  },
  {
    icon: () => 2,
    title: messages.houseTitle,
    description: messages.houseDescription,
    onClick: houseStepClicked({ photoSessionId, houseId, isNewChecklist }),
    chevron: true,
    status: isSectionsFinished.house ? status.SUCCESS : undefined,
  },
  {
    icon: () => 3,
    title: messages.generalConditionsTitle,
    description: canAssessProperty
      ? messages.propertyAssessmentDescription
      : messages.generalConditionsDescription,
    onClick:
      isSectionsFinished.house || !canAssessProperty
        ? generalConditionsStepClicked({
          photoSessionId,
          houseId,
          houseArea,
          canAssessProperty,
        })
        : undefined,
    chevron: isSectionsFinished.house || !canAssessProperty,
    status: isSectionsFinished.generalConditions ? status.SUCCESS : undefined,
  },
];

export const Steps = ({
  photoSessionId,
  houseId,
  progress,
  houseStepClicked,
  generalConditionsStepClicked,
  externalAreaStepClicked,
  isSectionsFinished,
  isNewChecklist,
}) => {
  const { data: canAssessProperty = false } =
    useCanAssessProperty(photoSessionId);
  const { data: photoSession } = usePhotoSession(photoSessionId);

  const itensStatusList = getItensStatusList({
    photoSessionId,
    houseId,
    houseStepClicked,
    generalConditionsStepClicked,
    externalAreaStepClicked,
    isSectionsFinished,
    isNewChecklist,
    canAssessProperty,
    houseArea: photoSession?.house?.areaTotal,
  });
  const percentageDone = progress.total
    ? Math.round((progress.current * 100) / progress.total)
    : 0;

  return (
    <>
      <Typography level="bodyStrong">
        <FormattedMessage {...messages.title} />
      </Typography>
      <Box my={1}>
        <Progress
          value={percentageDone}
          color={isChecklistFinished(percentageDone)}
        />
      </Box>
      <StatusList hideProgress items={itensStatusList} />
    </>
  );
};

export const mapDispatchToProps = (dispatch) => ({
  houseStepClicked:
    ({ houseId, photoSessionId, isNewChecklist }) =>
      () => {
        dispatch(
          houseStepClickedAnalytics({
            photoSessionId,
            houseId,
          }),
        );
        dispatch(
          push(
            isNewChecklist
              ? ROUTES.NEW_CHECKLIST_HOUSE(photoSessionId)
              : ROUTES.CHECKLIST_HOUSE(photoSessionId),
          ),
        );
      },
  generalConditionsStepClicked:
    ({ houseId, houseArea, photoSessionId, canAssessProperty }) =>
      () => {
        dispatch(
          generalConditionsClickedAnalytics({
            photoSessionId,
            houseId,
            houseArea,
            fullEvaluation: canAssessProperty ? 1 : 0,
          }),
        );

        if (canAssessProperty) {
          dispatch(resumeAssessment({ photoSessionId }));
        } else {
          dispatch(push(ROUTES.CHECKLIST_GENERAL_CONDITIONS(photoSessionId)));
        }
      },
  externalAreaStepClicked:
    ({ houseId, photoSessionId, isNewChecklist }) =>
      () => {
        dispatch(
          externalAreaClickedAnalytics({
            photoSessionId,
            houseId,
          }),
        );
        dispatch(
          push(
            isNewChecklist
              ? ROUTES.NEW_CHECKLIST_EXTERNAL_AREA(photoSessionId)
              : ROUTES.CHECKLIST_EXTERNAL_AREA(photoSessionId),
          ),
        );
      },
});

Steps.propTypes = {
  houseId: PropTypes.number,
  houseStepClicked: PropTypes.func,
  externalAreaStepClicked: PropTypes.func,
  generalConditionsStepClicked: PropTypes.func,
  photoSessionId: PropTypes.number,
  progress: PropTypes.number,
  isSectionsFinished: PropTypes.shape({
    house: PropTypes.bool,
    externalArea: PropTypes.bool,
    generalConditions: PropTypes.bool,
  }),
  isNewChecklist: PropTypes.bool,
};

export default connect(null, mapDispatchToProps)(Steps);
